import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import WorkspaceLoader from './WorkspaceLoader';
import {
  Grid, List, FileText, ArrowLeft, Search, Upload, Trash2, Eye, Download, X as XIcon, Heart
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { db, storage, auth } from '@/firebase/config';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  deleteDoc,
  doc,
  getDocs,
  setDoc,
} from 'firebase/firestore';
import { ref, getDownloadURL, deleteObject } from 'firebase/storage';

import {
  PDFReader,
  Resource,
} from './IndependentResourceComponents';
import { PersonalBookUploadForm } from './PersonalBookUploadForm';

// Local type that extends the shared Resource with a storage path field,
// so we can clean up the underlying file in Firebase Storage on delete.
type PersonalResource = Resource & { storagePath?: string };

const CourseMaterialsView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const state = (location.state || {}) as { from?: string; subjectId?: string; subjectName?: string };

  // Back button — use browser history so the previous page keeps its state
  // (e.g. selected subject on the lesson planner). Hard-navigating to state.from
  // re-renders the page without the in-memory state, which can fall through to
  // the dashboard if the subject isn't set.
  const handleBack = () => {
    navigate(-1);
  };

  // View state
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<'title' | 'uploadDate'>('uploadDate');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeType, setActiveType] = useState<string>('all');

  // Data
  const [userId, setUserId] = useState<string | null>(null);
  const [resources, setResources] = useState<Resource[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewResource, setViewResource] = useState<PersonalResource | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // Favourites — Set of personal resource IDs the user has hearted.
  // Mirrors the public view's `users/{uid}/bookmarks` pattern, but stored
  // inside the personal workspace so the data lives with the user's other
  // personal resources (teaching_workspaces/{uid}/favourites/{resourceId}).
  const [favourites, setFavourites] = useState<Set<string>>(new Set());
  const [favouritesFilter, setFavouritesFilter] = useState<boolean>(false);

  // Capture userId from auth
  useEffect(() => {
    const user = auth.currentUser;
    if (user) setUserId(user.uid);
  }, []);

  // Subscribe to favourites (live listener so changes from elsewhere — e.g. the
  // lesson planner's inline preview — are picked up immediately on this page).
  useEffect(() => {
    if (!userId) return;
    const favRef = collection(db, 'teaching_workspaces', userId, 'favourites');
    const unsubscribe = onSnapshot(favRef, (snapshot) => {
      setFavourites(new Set(snapshot.docs.map((d) => d.id)));
    }, (err) => {
      console.error('Favourites listener error:', err);
    });
    return () => unsubscribe();
  }, [userId]);

  // Subscribe to the personal resources subcollection
  useEffect(() => {
    if (!userId) return;
    setIsLoading(true);
    const ref = collection(db, 'teaching_workspaces', userId, 'personal_resources');
    const q = query(ref, orderBy('uploadDate', 'desc'));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const docs = snapshot.docs.map((d) => {
          const data = d.data() as any;
          return {
            id: d.id,
            title: data.title || data.name || 'Untitled',
            subject: data.subject || '',
            grade: data.grade || '',
            examType: data.examType || '',
            type: data.type || data.resourceType || 'other',
            description: data.description || '',
            tags: data.tags || [],
            author: data.author || '',
            isBookmarked: false,
            downloadCount: 0,
            fileType: data.fileType || (data.mime?.split('/')[1]?.toUpperCase() ?? 'FILE'),
            fileSize: data.fileSize || '',
            uploadDate: data.uploadDate || new Date().toISOString(),
            pdfUrl: data.pdfUrl || data.url || '',
            year: data.year || '',
            storagePath: data.storagePath || '',
          } as PersonalResource;
        });
        setResources(docs);
        setIsLoading(false);
      },
      (err) => {
        console.error('Error loading personal resources:', err);
        setIsLoading(false);
      }
    );
    return () => unsubscribe();
  }, [userId]);

  // Derive counts per type for the category chips
  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = { all: resources.length, favourites: favourites.size };
    for (const r of resources) {
      const t = r.type || 'other';
      counts[t] = (counts[t] || 0) + 1;
    }
    return counts;
  }, [resources, favourites.size]);

  // Filter + sort
  const filteredResources = useMemo(() => {
    let result = resources;
    if (favouritesFilter) {
      result = result.filter((r) => favourites.has(r.id));
    }
    if (activeType !== 'all') {
      result = result.filter((r) => (r.type || 'other') === activeType);
    }
    const q = searchQuery.toLowerCase().trim();
    if (q) {
      result = result.filter((r) =>
        r.title.toLowerCase().includes(q) ||
        r.subject.toLowerCase().includes(q) ||
        (r.tags || []).some((t) => t.toLowerCase().includes(q))
      );
    }
    result = [...result].sort((a, b) => {
      if (sortBy === 'title') return a.title.localeCompare(b.title);
      return new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime();
    });
    return result;
  }, [resources, activeType, searchQuery, sortBy, favouritesFilter, favourites]);

  // Known type chips (show the 4 most common BookUploadForm types + all, plus a Favourites chip at the end)
  const knownTypes = ['all', 'book', 'notes', 'guide', 'paper'] as const;

  const handleView = (resource: PersonalResource) => {
    setViewResource(resource);
  };

  const handleDownload = async (resource: Resource) => {
    setDownloadingId(resource.id);
    try {
      toast({
        title: 'Fetching file…',
        description: `Preparing "${resource.title}" for download.`,
        className: "bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800 text-green-800 dark:text-green-300",
        duration: 3000,
      });
      // Prefer the stored storagePath; fall back to pdfUrl for legacy docs
      const storagePath = (resource as PersonalResource).storagePath || resource.pdfUrl;
      const storageRef = ref(storage, storagePath);
      const downloadUrl = await getDownloadURL(storageRef);
      const response = await fetch(downloadUrl);
      if (!response.ok) throw new Error('Network response was not ok');
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      const cleanUrl = resource.pdfUrl.split('?')[0];
      const fileExtension = cleanUrl.split('.').pop()?.toLowerCase() || 'pdf';
      link.download = `${resource.title}.${fileExtension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (err) {
      console.error('Download error:', err);
      toast({
        title: 'Download Failed',
        description: 'Unable to download the file. Please try again.',
        className: "bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800 text-red-800 dark:text-red-300",
        duration: 4000,
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async (resource: Resource) => {
    if (!userId) return;
    const ok = window.confirm(`Delete "${resource.title}"? This cannot be undone.`);
    if (!ok) return;
    setDeletingId(resource.id);
    try {
      // Best-effort: remove the file from storage (using the stored storagePath)
      const storagePath = (resource as PersonalResource).storagePath;
      if (storagePath) {
        try {
          const storageRef = ref(storage, storagePath);
          await deleteObject(storageRef);
        } catch (e) {
          // Don't block Firestore delete on storage error
          console.warn('Storage delete skipped (file may be gone):', e);
        }
      }
      await deleteDoc(doc(db, 'teaching_workspaces', userId, 'personal_resources', resource.id));
      toast({ title: 'Deleted', description: `"${resource.title}" was removed.` });
    } catch (err) {
      console.error('Delete error:', err);
      toast({
        title: 'Delete Failed',
        description: 'Unable to delete the resource. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setDeletingId(null);
    }
  };

  // Toggle favourite for a personal resource.
  // Mirrors IndependentResourcesView's handleBookmark: stores a tiny doc at
  // teaching_workspaces/{uid}/favourites/{resourceId} so favourites are
  // namespaced with the user's other personal resources.
  const handleFavourite = async (resource: Resource) => {
    if (!userId) return;
    const isFav = favourites.has(resource.id);
    const ref = doc(db, 'teaching_workspaces', userId, 'favourites', resource.id);
    try {
      if (isFav) {
        await deleteDoc(ref);
        setFavourites((prev) => {
          const next = new Set(prev);
          next.delete(resource.id);
          return next;
        });
      } else {
        await setDoc(ref, { resourceId: resource.id, title: resource.title, createdAt: new Date().toISOString() });
        setFavourites((prev) => {
          const next = new Set(prev);
          next.add(resource.id);
          return next;
        });
      }
    } catch (err) {
      console.error('Favourite toggle error:', err);
      toast({ title: 'Error', description: 'Could not update favourite.', variant: 'destructive' });
    }
  };

  // If we're viewing a PDF, render the full reader
  if (viewResource) {
    return (
      <PDFReader
        pdfUrl={viewResource.pdfUrl}
        title={viewResource.title}
        onBack={() => { setViewResource(null); setIsFullscreen(false); }}
        setIsFullscreen={setIsFullscreen}
      />
    );
  }

  return (
    <div className="w-full pb-20">
      {/* Header: back button + title */}
      <header className="flex items-center gap-4 p-4 md:px-12 md:pt-6 pb-2">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={handleBack}
          title="Go back"
          aria-label="Go back"
          className="rounded-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm hover:bg-slate-100 dark:hover:bg-slate-800"
        >
          <ArrowLeft className="h-4 w-4 text-slate-700 dark:text-slate-300" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">
            My Materials
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Your personal library of schemes, textbooks, and handouts.
            {state?.subjectName ? (
              <> Viewing from <span className="font-bold text-indigo-600 dark:text-indigo-400">{state.subjectName}</span>.</>
            ) : null}
          </p>
        </div>
      </header>

      {/* Toolbar: search + grid/list + sort + upload */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-4 md:px-12 pt-4 pb-4 border-b border-slate-100 dark:border-slate-800/60">
        <div className="flex items-center gap-2 flex-1 w-full sm:w-auto">
          <div className="relative w-full sm:w-[280px]">
            <Input
              placeholder="Search my materials…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-600 pr-9"
            />
            {searchQuery ? (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-9 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                aria-label="Clear search"
              >
                <XIcon className="h-3.5 w-3.5" />
              </button>
            ) : null}
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant={viewMode === 'grid' ? 'default' : 'outline'}
            size="icon"
            onClick={() => setViewMode('grid')}
            aria-label="Grid view"
            className={cn(
              viewMode === 'grid'
                ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300'
            )}
          >
            <Grid className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'}
            size="icon"
            onClick={() => setViewMode('list')}
            aria-label="List view"
            className={cn(
              viewMode === 'list'
                ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300'
            )}
          >
            <List className="h-4 w-4" />
          </Button>

          <Select value={sortBy} onValueChange={(v) => setSortBy(v as 'title' | 'uploadDate')}>
            <SelectTrigger className="w-[150px] bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 text-sm">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800">
              <SelectItem value="title" className="text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/60 focus:bg-slate-100 dark:focus:bg-slate-800/60 cursor-pointer">Sort by Title</SelectItem>
              <SelectItem value="uploadDate" className="text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/60 focus:bg-slate-100 dark:focus:bg-slate-800/60 cursor-pointer">Sort by Date</SelectItem>
            </SelectContent>
          </Select>

          <Button
            type="button"
            onClick={() => setIsUploadOpen(true)}
            disabled={!userId}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            <Upload className="h-4 w-4 mr-2" /> Upload
          </Button>
        </div>
      </div>

      {/* Type chip strip */}
      {resources.length > 0 && (
        <div className="flex items-center gap-2 px-4 md:px-12 pt-4 pb-2 flex-wrap">
          {knownTypes.map((t) => {
            const count = typeCounts[t] || 0;
            const isActive = activeType === t;
            const label = t === 'all' ? 'All' : t.charAt(0).toUpperCase() + t.slice(1);
            return (
              <button
                key={t}
                type="button"
                onClick={() => setActiveType(t)}
                className={cn(
                  'inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-bold uppercase tracking-wider transition-colors',
                  isActive
                    ? 'bg-indigo-600 text-white'
                    : 'bg-slate-100 dark:bg-slate-800/60 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800'
                )}
              >
                {label}
                <Badge
                  variant="secondary"
                  className={cn(
                    'h-4 px-1 text-[9px] font-black',
                    isActive
                      ? 'bg-white/20 text-white hover:bg-white/20'
                      : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700'
                  )}
                >
                  {count}
                </Badge>
              </button>
            );
          })}
          {/* Favourites chip — toggles the favourites-only filter, sits after the Paper chip */}
          <button
            type="button"
            onClick={() => setFavouritesFilter((v) => !v)}
            aria-pressed={favouritesFilter}
            className={cn(
              'inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-bold uppercase tracking-wider transition-colors',
              favouritesFilter
                ? 'bg-rose-500 text-white hover:bg-rose-600'
                : 'bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-300 hover:bg-rose-100 dark:hover:bg-rose-500/20'
            )}
          >
            <Heart className={cn('h-3.5 w-3.5', favouritesFilter && 'fill-current')} />
            Favourites
            <Badge
              variant="secondary"
              className={cn(
                'h-4 px-1 text-[9px] font-black',
                favouritesFilter
                  ? 'bg-white/20 text-white hover:bg-white/20'
                  : 'bg-rose-100 dark:bg-rose-500/20 text-rose-700 dark:text-rose-200 hover:bg-rose-100 dark:hover:bg-rose-500/20'
              )}
            >
              {favourites.size}
            </Badge>
          </button>
        </div>
      )}

      {/* Body */}
      <div className="px-4 md:px-12 pt-4">
        {isLoading ? (
          <WorkspaceLoader minHeight="min-h-[200px]" />
        ) : filteredResources.length === 0 ? (
          <div className="text-center py-16 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-3xl">
            <FileText className="mx-auto h-12 w-12 text-slate-400 dark:text-slate-600 mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-200">
              {favouritesFilter
                ? 'No favourites yet'
                : searchQuery || activeType !== 'all'
                ? 'No matching materials'
                : 'No personal materials yet'}
            </h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
              {favouritesFilter
                ? 'Tap the heart icon on any material to add it to your favourites.'
                : searchQuery || activeType !== 'all'
                ? 'Try a different search term or clear the type filter.'
                : 'Click "Upload" above to add your first scheme, textbook, or handout. Files you upload here stay private to you.'}
            </p>
            {!searchQuery && !favouritesFilter && activeType === 'all' && (
              <Button
                type="button"
                onClick={() => setIsUploadOpen(true)}
                disabled={!userId}
                className="mt-4 bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                <Upload className="h-4 w-4 mr-2" /> Upload your first material
              </Button>
            )}
          </div>
        ) : (
          <>
            {/* Grid view */}
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredResources.map((r) => (
                  <PersonalResourceCard
                    key={r.id}
                    resource={r}
                    onView={handleView}
                    onDownload={handleDownload}
                    onDelete={handleDelete}
                    onFavourite={handleFavourite}
                    isFavourite={favourites.has(r.id)}
                    isDownloading={downloadingId === r.id}
                    isDeleting={deletingId === r.id}
                  />
                ))}
              </div>
            ) : (
              /* List view */
              <div className="space-y-2">
                {filteredResources.map((r) => (
                  <PersonalResourceListRow
                    key={r.id}
                    resource={r}
                    onView={handleView}
                    onDownload={handleDownload}
                    onDelete={handleDelete}
                    onFavourite={handleFavourite}
                    isFavourite={favourites.has(r.id)}
                    isDownloading={downloadingId === r.id}
                    isDeleting={deletingId === r.id}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Upload dialog */}
      <Dialog open={isUploadOpen} onOpenChange={setIsUploadOpen}>
        <DialogContent className="max-w-3xl w-[calc(100%-2rem)] bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2 text-indigo-600 dark:text-indigo-400">
              <Upload className="h-5 w-5" /> Upload to My Materials
            </DialogTitle>
          </DialogHeader>
          <PersonalBookUploadForm
            userId={userId!}
            subjectId={state?.subjectId}
            subjectNameFallback={state?.subjectName}
            onClose={() => setIsUploadOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export { PersonalResourceCard };
export default CourseMaterialsView;

// ----- Local card / row components (personal-scope; no bookmarks, no public category colors) -----

function typeColor(type: string) {
  switch (type) {
    case 'book':
      return 'bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-500/30';
    case 'paper':
      return 'bg-slate-100 dark:bg-slate-700/40 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-600';
    case 'guide':
      return 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/30';
    case 'notes':
      return 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-500/30';
    default:
      return 'bg-slate-100 dark:bg-slate-700/40 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-600';
  }
}

function typeLabel(type: string) {
  if (!type) return 'Other';
  return type.charAt(0).toUpperCase() + type.slice(1);
}

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

function PersonalResourceCard({
  resource,
  onView,
  onDownload,
  onDelete,
  onFavourite,
  isFavourite,
  isDownloading,
  isDeleting,
}: {
  resource: Resource;
  onView: (r: Resource) => void;
  onDownload: (r: Resource) => void;
  onDelete: (r: Resource) => void;
  onFavourite: (r: Resource) => void;
  isFavourite: boolean;
  isDownloading: boolean;
  isDeleting: boolean;
}) {
  return (
    <div className="group flex flex-col p-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl hover:shadow-md transition-shadow min-h-[180px]">
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className={cn('inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] font-black uppercase tracking-wider', typeColor(resource.type))}>
          {typeLabel(resource.type)}
        </span>
        <div className="flex gap-1">
          <Button
            size="icon"
            variant="ghost"
            onClick={() => onFavourite(resource)}
            className={cn(
              'h-7 w-7',
              isFavourite
                ? 'text-rose-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10'
                : 'text-slate-400 dark:text-slate-500 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10'
            )}
            title={isFavourite ? 'Remove from favourites' : 'Add to favourites'}
            aria-pressed={isFavourite}
            aria-label={isFavourite ? 'Remove from favourites' : 'Add to favourites'}
          >
            <Heart className={cn('h-3.5 w-3.5', isFavourite && 'fill-current')} />
          </Button>
          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
            <Button size="icon" variant="ghost" onClick={() => onDelete(resource)} disabled={isDeleting} className="h-7 w-7 text-rose-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10" title="Delete">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>
      <h3 className="font-semibold text-sm mb-1 line-clamp-2 text-slate-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
        {resource.title}
      </h3>
      {resource.description ? (
        <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{resource.description}</p>
      ) : (
        <p className="text-xs text-muted-foreground mb-3 line-clamp-2">No description.</p>
      )}
      <div className="mt-auto flex items-center justify-between text-[10px] text-slate-500 dark:text-slate-400">
        <span className="font-medium uppercase tracking-wider">
          {resource.fileType || 'FILE'}{resource.fileSize ? ` • ${resource.fileSize}` : ''}
        </span>
        <span>{formatDate(resource.uploadDate)}</span>
      </div>
      <div className="flex gap-2 mt-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onView(resource)}
          className="flex-1 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-indigo-600 dark:hover:text-indigo-400"
        >
          <Eye className="h-3.5 w-3.5 mr-1.5" /> View
        </Button>
        <Button
          size="sm"
          onClick={() => onDownload(resource)}
          disabled={isDownloading}
          className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
        >
          {isDownloading ? (
            <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent mr-1.5" />
          ) : (
            <Download className="h-3.5 w-3.5 mr-1.5" />
          )}
          {isDownloading ? 'Downloading…' : 'Download'}
        </Button>
      </div>
    </div>
  );
}

function PersonalResourceListRow({
  resource,
  onView,
  onDownload,
  onDelete,
  onFavourite,
  isFavourite,
  isDownloading,
  isDeleting,
}: {
  resource: Resource;
  onView: (r: Resource) => void;
  onDownload: (r: Resource) => void;
  onDelete: (r: Resource) => void;
  onFavourite: (r: Resource) => void;
  isFavourite: boolean;
  isDownloading: boolean;
  isDeleting: boolean;
}) {
  return (
    <div className="group flex items-center justify-between p-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <span className={cn('inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] font-black uppercase tracking-wider shrink-0', typeColor(resource.type))}>
          {typeLabel(resource.type)}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-sm text-slate-900 dark:text-white truncate">{resource.title}</h3>
          <p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wider truncate">
            {resource.fileType || 'FILE'}{resource.fileSize ? ` • ${resource.fileSize}` : ''} • {formatDate(resource.uploadDate)}
          </p>
        </div>
      </div>
      <div className="flex gap-1 shrink-0">
        <Button
          size="icon"
          variant="ghost"
          onClick={() => onFavourite(resource)}
          className={cn(
            'h-8 w-8',
            isFavourite
              ? 'text-rose-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10'
              : 'text-slate-400 dark:text-slate-500 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 opacity-0 group-hover:opacity-100 transition-opacity'
          )}
          title={isFavourite ? 'Remove from favourites' : 'Add to favourites'}
          aria-pressed={isFavourite}
          aria-label={isFavourite ? 'Remove from favourites' : 'Add to favourites'}
        >
          <Heart className={cn('h-4 w-4', isFavourite && 'fill-current')} />
        </Button>
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button size="icon" variant="ghost" onClick={() => onView(resource)} className="h-8 w-8 text-slate-700 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400" title="View">
            <Eye className="h-4 w-4" />
          </Button>
          <Button size="icon" variant="ghost" onClick={() => onDownload(resource)} disabled={isDownloading} className="h-8 w-8 text-slate-700 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400" title="Download">
            {isDownloading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-indigo-600 border-t-transparent" />
            ) : (
              <Download className="h-4 w-4" />
            )}
          </Button>
          <Button size="icon" variant="ghost" onClick={() => onDelete(resource)} disabled={isDeleting} className="h-8 w-8 text-rose-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10" title="Delete">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
